// ProductDetail Page - Master product with supplier comparison
import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Package,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Navigation,
  ChevronDown,
  Heart,
} from 'lucide-react'
import * as ToggleGroup from '@radix-ui/react-toggle-group'
import clsx from 'clsx'
import { EngineerLayout } from '../../components/layout/EngineerLayout'
import { Button, QuantitySelector, SupplierListingCard, useToast } from '../../components/radix'
import type { FulfillmentMethod } from '../../components/radix/SupplierListingCard'
import { useCart } from '../../auth/hooks/useCart'
import { useFavourites } from '../../auth/hooks/useFavourites'
import { useFavouriteToggle } from '../../components/engineer/favourites/useFavouriteToggle'
import { usePostcodeSync } from '../../hooks/usePostcodeSync'
import { api } from '../../services/api'
import {
  ProductViewType,
  clearProductViewSource,
  resolveProductViewSource,
  trackProductViewed,
  trackStandardProductAdd,
} from '../../lib/analytics'
import type { MasterProduct, SupplierListing, FulfillmentOption } from '../../types'
import { PRODUCT_ADD_MODES } from '../../constants/quickAdd'
import { formatEarliestDate } from '../../utils/datetime'
import { perUnitPrice } from '../../utils/pricing'
import { toastClearanceRef } from '../../utils/heightVarRef'
import {
  removeItemFulfillmentMethod,
  writeCartFulfillmentMethod,
  writeItemFulfillmentMethod,
} from '../../utils/cartFulfillment'

interface EngineerSession {
  postcode: string
  latitude: number
  longitude: number
}

export function ProductDetail() {
  const navigate = useNavigate()
  const { tenantSlug, masterProductId } = useParams()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  // True when the user navigated here with the in-stock filter active on the
  // search page. When set, supplier offerings with zero stock are hidden.
  const inStockFilterActive = searchParams.get('inStock') === '1'
  // DEV-50739 SP-003: a prioritised search card passes the basket branch it
  // matched so this page opens with that supplier + branch selected instead
  // of the default nearest group. Applied once, and never over a branch the
  // engineer has since picked by hand.
  const preferredBranchId = searchParams.get('branch')
  const preferredBranchAppliedRef = useRef(false)
  const manualSelectionRef = useRef(false)
  const { addToast } = useToast()
  const { addToCart: addToCartMutation, updateCartLine, cart, isAdding, isUpdating } = useCart()
  const queryClient = useQueryClient()

  const [session, setSession] = useState<EngineerSession | null>(null)
  // Analytics: report product_viewed once per opened master product. Keyed on
  // the id so navigating to a different master fires again, but a re-render,
  // scroll or tab switch (same id) does not.
  const viewedMasterRef = useRef<string | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [showAllListings, setShowAllListings] = useState(false)
  const [showDescription, setShowDescription] = useState(false)
  const [listingSort, setListingSort] = useState<'distance' | 'price'>('distance')
  // Page-level offer selection. The card is the picker; the sticky footer is
  // the actioner. `selection` identifies which supplier (productId) + which
  // branch + which fulfilment method is currently active; `quantity` seeds
  // both the footer stepper and the card's Order-on-Demand DC recheck.
  const [selection, setSelection] = useState<{
    productId: string | number
    branchId: string | number | null
    method: FulfillmentMethod
  } | null>(null)
  const [quantity, setQuantity] = useState<number>(1)
  // Effective pickup/delivery dates the card reports for each branch — DC
  // date when qty exceeds branch stock, else the branch-level earliestDate.
  // Also carries `isChecking` so the page-level Add button can wait for the
  // DC-quantity check to resolve. Keyed by `${supplierId}-${branchId}`.
  type ResolvedDates = {
    pickup: string | null
    delivery: string | null
    isChecking: boolean
    backorderPickup: string | null
    backorderDelivery: string | null
  }
  const [resolvedDatesByBranch, setResolvedDatesByBranch] = useState<
    Map<string, ResolvedDates>
  >(new Map())
  const handleResolvedDates = useCallback(
    (listing: SupplierListing, dates: ResolvedDates) => {
      const key = `${listing.supplierId}-${listing.branchId}`
      setResolvedDatesByBranch((prev) => {
        const existing = prev.get(key)
        if (
          existing &&
          existing.pickup === dates.pickup &&
          existing.delivery === dates.delivery &&
          existing.isChecking === dates.isChecking &&
          existing.backorderPickup === dates.backorderPickup &&
          existing.backorderDelivery === dates.backorderDelivery
        ) {
          return prev
        }
        const next = new Map(prev)
        next.set(key, dates)
        return next
      })
    },
    [],
  )

  // BM-002: basket-boost keys. Cards for a supplier the engineer already has
  // items from float above the rest, and branches the engineer already picked
  // float above other branches of the same supplier. Consolidating a single
  // trip is more valuable than saving a mile or a pound. Empty basket → both
  // sets are empty and the boost logic no-ops (AC6).
  const basketBranchIds = useMemo(
    () =>
      new Set(
        cart
          .map((item) => (item.branchId != null ? String(item.branchId) : ''))
          .filter(Boolean),
      ),
    [cart],
  )
  const basketSupplierIds = useMemo(
    () =>
      new Set(
        cart
          .map((item) => (item.supplierId != null ? String(item.supplierId) : ''))
          .filter(Boolean),
      ),
    [cart],
  )

  // Load session on mount
  useEffect(() => {
    const savedSession = sessionStorage.getItem('engineerSession')
    if (savedSession) {
      setSession(JSON.parse(savedSession))
    } else {
      navigate(`/${tenantSlug}/engineer`)
    }
  }, [navigate, tenantSlug])

  // Check if postcode changed on another device (cross-device sync)
  usePostcodeSync(session)

  // Set initial image to thumbnail when product loads
  useEffect(() => {
    if (!product) return

    if (product.images && product.images.length > 0) {
      // Find thumbnail image index
      const thumbnailIndex = product.images.findIndex(img => img.is_thumbnail)
      // Set to thumbnail if found, otherwise default to 0
      setCurrentImageIndex(thumbnailIndex >= 0 ? thumbnailIndex : 0)
    } else {
      setCurrentImageIndex(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterProductId])

  // Fetch master product. Location is included so the backend can flag
  // suppliers with no branch in range (drives the reliable "No nearby branch"
  // tag). Keyed on location so a postcode change refetches the flags.
  const { data: product, isLoading, error } = useQuery({
    queryKey: [
      'master-product',
      masterProductId,
      session?.postcode,
      session?.latitude,
      session?.longitude,
    ],
    queryFn: () =>
      api.getMasterProduct(
        masterProductId!,
        session?.postcode,
        session?.latitude,
        session?.longitude,
      ),
    enabled: !!session && !!masterProductId,
  })

  // Analytics: master-product view, once the product has loaded and once per
  // id. supplier/raw fields are null — no supplier is chosen on this page.
  useEffect(() => {
    if (!product || !masterProductId) return
    if (viewedMasterRef.current === masterProductId) return
    viewedMasterRef.current = masterProductId
    trackProductViewed({
      type: ProductViewType.MASTER,
      rawProductId: null,
      rawProductName: null,
      masterProductId: String(masterProductId),
      masterProductName: product.displayName,
      supplier: null,
      supplierId: null,
      source: resolveProductViewSource(location.state),
    })
    // A reload restores this source from history; a reload is a direct visit,
    // so forget it now that the view is recorded.
    clearProductViewSource()
  }, [product, masterProductId, location.state])

  // Get raw product IDs for live data fetching
  const rawProductIds = product?.supplierListings.map(l => l.productId) || []

  // Fetch live prices for all supplier listings
  const { data: livePricesData } = useQuery({
    queryKey: ['live-prices', rawProductIds],
    queryFn: () => api.getLivePrices(rawProductIds as number[]),
    enabled: !!product && rawProductIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
  })

  // When live prices arrive, patch every cached search-master-products query
  // in place so back-navigating to the search page shows the fresh price
  // instead of the stale hyphen. The search response cache does not know
  // about the live-price write; patching React Query directly is surgical —
  // only this master's row is touched, no refetch fires, no other card
  // moves. On mount of the search page the patched data is served straight
  // from the cache (5-min staleTime).
  useEffect(() => {
    if (!product || !masterProductId || !livePricesData?.prices?.length) return

    const supplierNameByRawId = new Map<string, string>()
    for (const supplierListing of product.supplierListings) {
      supplierNameByRawId.set(String(supplierListing.productId), supplierListing.supplierName)
    }

    let lowestLivePrice: number | null = null
    let lowestLivePriceSupplierName: string | null = null
    for (const priceEntry of livePricesData.prices) {
      if (priceEntry.unavailable || priceEntry.soft_deleted) continue
      const parsedPrice = parseFloat(priceEntry.selling_price)
      if (!Number.isFinite(parsedPrice)) continue
      if (lowestLivePrice === null || parsedPrice < lowestLivePrice) {
        lowestLivePrice = parsedPrice
        lowestLivePriceSupplierName =
          supplierNameByRawId.get(String(priceEntry.raw_product_id)) ?? null
      }
    }

    if (lowestLivePrice === null) return

    queryClient.setQueriesData<{ products: MasterProduct[] } | undefined>(
      { queryKey: ['search-master-products'] },
      (existing) => {
        if (!existing?.products) return existing
        let didPatch = false
        const patchedProducts = existing.products.map((cardProduct) => {
          if (String(cardProduct.id) !== String(masterProductId)) return cardProduct
          if (
            cardProduct.lowestPrice === lowestLivePrice &&
            cardProduct.lowestPriceSupplierName === lowestLivePriceSupplierName
          ) {
            return cardProduct
          }
          didPatch = true
          return {
            ...cardProduct,
            lowestPrice: lowestLivePrice as number,
            lowestPriceSupplierName: lowestLivePriceSupplierName,
          }
        })
        return didPatch ? { ...existing, products: patchedProducts } : existing
      },
    )
  }, [livePricesData, product, masterProductId, queryClient])

  // Fetch live stock — one parallel request PER SUPPLIER so a slow
  // supplier doesn't block stock for the others. Each supplier's section
  // has its own loading/error state so add-to-cart unlocks per supplier
  // as soon as that supplier resolves. Per-listing merge below uses the
  // per-supplier lookup directly instead of a combined `liveStockData`
  // shim that would have to flatten then re-filter on every render.
  const rawProductIdsBySupplier = useMemo(() => {
    const map = new Map<number, (number | string)[]>()
    for (const listing of product?.supplierListings ?? []) {
      // Skip suppliers the backend flagged as out of range — their live-stock
      // call would return empty anyway, and their card is rendered from the
      // authoritative flag below.
      if (listing.noNearbyBranch) continue
      const supplierId =
        typeof listing.supplierId === 'string'
          ? parseInt(listing.supplierId, 10)
          : listing.supplierId
      if (!Number.isFinite(supplierId)) continue
      const arr = map.get(supplierId as number) ?? []
      arr.push(listing.productId)
      map.set(supplierId as number, arr)
    }
    return map
  }, [product?.supplierListings])

  const supplierIdsInOrder = useMemo(
    () => Array.from(rawProductIdsBySupplier.keys()).sort((a, b) => a - b),
    [rawProductIdsBySupplier],
  )

  const supplierStockQueries = useQueries({
    queries: supplierIdsInOrder.map((supplierId) => {
      const ids = rawProductIdsBySupplier.get(supplierId) ?? []
      return {
        queryKey: [
          'live-stock',
          supplierId,
          ids,
          session?.postcode,
          session?.latitude,
          session?.longitude,
        ],
        queryFn: () =>
          api.getLiveStock(
            ids,
            session!.postcode,
            session!.latitude,
            session!.longitude,
            supplierId,
          ),
        enabled:
          !!product &&
          ids.length > 0 &&
          !!session?.postcode,
        staleTime: 2 * 60 * 1000,
        retry: 2,
      }
    }),
  })

  // Per-supplier loading/error/data lookup. The merge code below uses
  // this to determine PER-LISTING whether its supplier is still loading
  // — instead of a single global flag that gated every card on the
  // slowest supplier.
  const supplierStockBySupplierId = useMemo(() => {
    const map = new Map<
      number,
      { isLoading: boolean; isError: boolean; stock: any[] | null }
    >()
    supplierIdsInOrder.forEach((supplierId, idx) => {
      const q = supplierStockQueries[idx]
      const stockArr =
        q?.data && Array.isArray(q.data.stock) ? q.data.stock : null
      map.set(supplierId, {
        isLoading: q?.isLoading ?? false,
        isError: q?.isError ?? false,
        stock: stockArr,
      })
    })
    return map
  }, [supplierIdsInOrder, supplierStockQueries])

  // Global "stock check failed" indicator — only true when every
  // queried supplier errored AND no supplier successfully returned
  // stock. Mirrors the previous single-call `isStockError` semantics.
  // Per-supplier failure indicators on individual cards still surface
  // through `supplierIsError` inside the merge code below.
  const isStockError = useMemo(() => {
    if (supplierIdsInOrder.length === 0) return false
    let allErrored = true
    for (const value of supplierStockBySupplierId.values()) {
      if (value.isLoading) {
        allErrored = false
        break
      }
      if (!value.isError) {
        allErrored = false
        break
      }
    }
    return allErrored
  }, [supplierIdsInOrder, supplierStockBySupplierId])

  // Merge live prices AND live stock with supplier listings
  // IMPORTANT: Stock API may return MULTIPLE branches for same product_id
  // Create separate cards for each branch
  const listingsWithLiveData: SupplierListing[] = (product?.supplierListings || []).flatMap(rawListing => {
    // Find live price for this product
    const livePrice = livePricesData?.prices?.find(
      p => String(p.raw_product_id) === String(rawListing.productId)
    )

    // Product was soft-deleted during this price fetch (supplier no longer recognises it)
    // Drop it immediately so the engineer doesn't see a stale "price unavailable" listing
    if (livePrice?.soft_deleted) return []

    // CEF returns a fresh quantity_multiple with its live price; merge it into
    // the listing so every branch card below (which spreads ...listing) shows a
    // consistent per-unit price. Other suppliers omit it, so the cached
    // catalogue value is preserved unchanged.
    const listing = livePrice?.quantity_multiple != null
      ? { ...rawListing, quantityMultiple: livePrice.quantity_multiple }
      : rawListing

    // Per-supplier stock state — replaces the previous global flag. Slow
    // suppliers no longer block listings from other suppliers. Listings
    // whose supplier hasn't responded yet stay in 'checking'; once their
    // supplier's request resolves, they unlock independently.
    const listingSupplierId =
      typeof listing.supplierId === 'string'
        ? parseInt(listing.supplierId, 10)
        : listing.supplierId
    const supplierState =
      Number.isFinite(listingSupplierId)
        ? supplierStockBySupplierId.get(listingSupplierId as number)
        : undefined
    const supplierIsLoading = supplierState?.isLoading ?? true
    const supplierIsError = supplierState?.isError ?? false
    const supplierStock = supplierState?.stock ?? null

    // Find ALL stock entries for this product (multiple branches possible)
    const stockEntries =
      supplierStock?.filter(
        s => String(s.raw_product_id) === String(listing.productId)
      ) || []

    // Product was soft-deleted during this stock fetch (Wolseley answered -1 =
    // invalid product code at every branch). Drop it immediately, mirroring the
    // live-price soft_deleted path above, so the engineer never sees a stale
    // "stock not available" card for a product the supplier no longer sells.
    if (stockEntries.length > 0 && stockEntries.every(s => s.soft_deleted === true)) {
      return []
    }

    // Determine price from live data
    const priceUnavailable = livePrice?.unavailable === true
    const resolvedPrice = (livePrice && livePrice.selling_price && !priceUnavailable)
      ? parseFloat(livePrice.selling_price)
      : listing.price

    // Backend-authoritative "no branch in range". Rendered directly and BEFORE
    // any stock-outcome logic, so it's never mislabelled as "Stock not
    // available" when that supplier's (skipped) live-stock call resolves empty
    // or errors. No stock call is even fired for these suppliers.
    if (listing.noNearbyBranch) {
      return [{
        ...listing,
        branchId: null,
        branchName: '',
        distanceMiles: null,
        price: resolvedPrice,
        isLivePrice: !!livePrice && !priceUnavailable,
        priceUnavailable,
        stockQuantity: 0,
        stockStatus: 'unavailable' as const,
        noNearbyBranch: true,
        fulfillmentOptions: [],
      }] as SupplierListing[]
    }

    // While loading stock, show greyed/checking state (pessimistic UI)
    // Products are greyed by default, ungrey when stock is confirmed
    if (supplierIsLoading) {
      return [{
        ...listing,
        branchId: null,        // Don't show branch until stock confirms it
        branchName: '',        // Don't show branch name while loading
        distanceMiles: null,   // Don't show distance while loading
        price: resolvedPrice,
        isLivePrice: !!livePrice && !priceUnavailable,
        priceUnavailable,
        stockQuantity: 0,
        stockStatus: 'checking' as const,  // Checking state - greyed by default
        fulfillmentOptions: [
          { type: 'pickup' as const, available: false, stockQuantity: 0, stockStatus: 'checking' as const },
          { type: 'delivery' as const, available: false, stockQuantity: 0, stockStatus: 'checking' as const },
        ],
        isLoadingStock: true,  // Flag for card to show loading indicator
      }] as SupplierListing[]
    }

    // If stock API failed OR returned no/invalid data, show stock unavailable state
    // - supplierIsError: this supplier's query threw an error
    // - supplierStock === null: API returned null/undefined for this supplier
    const stockDataMissing = supplierStock === null
    if (supplierIsError || stockDataMissing) {
      return [{
        ...listing,
        branchId: null,
        branchName: '',
        distanceMiles: null,
        stockQuantity: 0,
        stockStatus: 'out_of_stock' as const,
        price: resolvedPrice,
        isLivePrice: !!livePrice && !priceUnavailable,
        priceUnavailable,
        stockUnavailable: true,  // Flag for card to show "Stock not available"
        fulfillmentOptions: [],
      }] as SupplierListing[]
    }

    // If stock loaded but no branches found — the supplier has no resolvable
    // branch for this postcode (e.g. supplier has zero active branches).
    // Instead of dropping the card, surface a disabled "No nearby branch"
    // listing so the engineer still sees the product exists (they arrived
    // here from search, where it was pinned to the bottom) — just not
    // orderable at this postcode.
    if (stockEntries.length === 0) {
      return [{
        ...listing,
        branchId: null,
        branchName: '',
        distanceMiles: null,
        price: resolvedPrice,
        isLivePrice: !!livePrice && !priceUnavailable,
        priceUnavailable,
        stockQuantity: 0,
        stockStatus: 'unavailable' as const,
        noNearbyBranch: true,
        fulfillmentOptions: [],
      }] as SupplierListing[]
    }

    // Create one listing PER BRANCH
    return stockEntries.map(liveStock => {
      // Check if stock API returned an error for this product
      const hasStockError = !!(liveStock as any).error
      const stockErrorMsg = hasStockError ? (liveStock as any).error : null

      // If there's an error, return listing with error state (not unavailable, not loading)
      if (hasStockError) {
        return {
          ...listing,
          // Live price
          price: resolvedPrice,
          isLivePrice: !!livePrice && !priceUnavailable,
          priceUnavailable,
          // Error state - no branch info available
          branchId: null,
          branchName: null,
          distanceMiles: null,
          stockQuantity: null,
          stockStatus: 'unavailable' as const,
          fulfillmentOptions: [
            { type: 'pickup' as const, available: false, stockQuantity: null, stockStatus: 'unavailable' as const },
            { type: 'delivery' as const, available: false, stockQuantity: null, stockStatus: 'unavailable' as const },
          ],
          isLiveStock: false,
          isLoadingStock: false,  // NOT loading - we have response (it's just an error)
          stockUnavailable: false,  // NOT unavailable - it's an ERROR (different state)
          stockError: stockErrorMsg, // Pass error message to card - THIS is what shows red
        } as SupplierListing
      }

      // Check if stock is unavailable but branch info exists (API failed)
      const isStockUnavailable = !!(liveStock as any).stock_unavailable
      if (isStockUnavailable) {
        const distanceMiles = (liveStock as any).distance_miles ?? listing.distanceMiles
        return {
          ...listing,
          // Live price
          price: resolvedPrice,
          isLivePrice: !!livePrice && !priceUnavailable,
          priceUnavailable,
          // Branch info available but stock unavailable
          branchId: liveStock.branch_id as string | number,
          branchName: liveStock.branch_name || '',
          distanceMiles: distanceMiles,
          stockQuantity: 0,
          stockStatus: 'out_of_stock' as const,
          fulfillmentOptions: [
            { type: 'pickup' as const, available: false, stockQuantity: 0, stockStatus: 'out_of_stock' as const },
            { type: 'delivery' as const, available: false, stockQuantity: 0, stockStatus: 'out_of_stock' as const },
          ],
          isLiveStock: true,
          stockUnavailable: true,  // Show AMBER "(-)" with "Stock not available" message
        } as SupplierListing
      }

      const stockQuantity = liveStock.stock_quantity ?? 0
      const distanceMiles = (liveStock as any).distance_miles ?? listing.distanceMiles

      // Prefer split fulfilment stock from backend; fall back to the combined
      // value so legacy adapters that don't split still render correctly.
      const collectionQty = (liveStock as any).collection_stock ?? stockQuantity
      const deliveryQty = (liveStock as any).delivery_stock ?? stockQuantity

      // Build fulfillment options from live stock data. Pull through the
      // earliest collection / delivery dates the supplier exposed (CPS,
      // Wolseley); other suppliers leave them undefined and the SupplierListingCard
      // falls back to no date label.
      const fulfillmentOptions: FulfillmentOption[] = [
        {
          type: 'pickup' as const,
          available: true,
          stockQuantity: collectionQty,
          stockStatus: collectionQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: (liveStock as any).earliest_collection,
        },
        {
          type: 'delivery' as const,
          available: true,
          stockQuantity: deliveryQty,
          stockStatus: deliveryQty > 0 ? 'in_stock' as const : 'out_of_stock' as const,
          earliestDate: (liveStock as any).earliest_delivery,
        },
      ]

      return {
        ...listing,
        // Live price
        price: resolvedPrice,
        isLivePrice: !!livePrice && !priceUnavailable,
        priceUnavailable,
        // Live stock - specific branch
        branchId: liveStock.branch_id as string | number,
        branchName: liveStock.branch_name || '',
        distanceMiles: distanceMiles,
        stockQuantity,
        stockStatus: stockQuantity > 0 ? 'in_stock' as const : 'out_of_stock' as const,
        fulfillmentOptions,
        // Order-on-Demand branch vs depot breakdown (master detail display).
        branchStock: (liveStock as any).branch_stock ?? null,
        dcStock: (liveStock as any).dc_stock ?? null,
        totalStock: (liveStock as any).total_stock ?? null,
        // Phase 2: explicit per-channel DC dates.
        pickupDcDate: (liveStock as any).pickup_dc_date ?? null,
        deliveryDcDate: (liveStock as any).delivery_dc_date ?? null,
        // Phase 3: explicit per-channel LATER-tile qtys.
        pickupDcStock: (liveStock as any).pickup_dc_stock ?? null,
        deliveryDcStock: (liveStock as any).delivery_dc_stock ?? null,
        // Phase 4: view-computed cap (branch + pickup_dc_stock).
        capTotal: (liveStock as any).cap_total ?? null,
        canCollect: (liveStock as any).can_collect ?? true,
        canDeliver: (liveStock as any).can_deliver ?? true,
        // Backorder tier — backend-decided; absent on older responses = off.
        backorderAllowed: liveStock.backorder_allowed === true,
        backorderCollectionDate: liveStock.backorder_collection_date ?? null,
        backorderDeliveryDate: liveStock.backorder_delivery_date ?? null,
        backorderDateFromApi: liveStock.backorder_date_from_api === true,
        isLiveStock: true,
        // Prefer the supplier's live UOM display when the stock
        // response carries one (currently Travis Perkins, resolved to
        // standard format on the backend). Other suppliers leave it
        // null, so the cached catalogue value is preserved.
        unitOfMeasure: (liveStock as any).uom ?? listing.unitOfMeasure,
      } as SupplierListing
    })
  }) || []

  // Order-on-Demand: live quantity-specific availability check. When the
  // engineer requests more than the branch holds, re-probe that one
  // product/branch with the real quantity (bypassing the cached qty-1 entry)
  // and hand back the earliest collection date for that quantity.
  const handleCheckQuantity = useCallback(
    async (
      supplierId: string | number,
      productCode: string,
      branchId: string | number | null,
      quantity: number,
    ): Promise<{
      collection: string | null
      delivery: string | null
      backorderCollection: string | null
      backorderDelivery: string | null
    }> => {
      const res = (await api.checkStock({
        supplier_id: Number(supplierId),
        product_codes: [productCode],
        branch_id: branchId != null ? String(branchId) : undefined,
        postcode: session?.postcode,
        quantity,
        bypass_cache: true,
      })) as {
        items?: Array<{
          earliest_collection?: string | null
          earliest_delivery?: string | null
          dc_available_date?: string | null
        }>
      }
      const item = res?.items?.[0]
      // Both map to the DC LATER tier in the card. Use ONLY the explicit
      // DC dates — falling back to earliest_collection / earliest_delivery
      // would surface the BRANCH date as a DC date (e.g. "Today" instead of
      // the real depot ETA).
      return {
        collection: item?.pickup_dc_date ?? null,
        delivery: item?.delivery_dc_date ?? null,
        // Backorder (API-date suppliers): the supplier's earliest dates at the
        // REQUESTED qty are its backorder quote. Only read by the card when
        // the line is a backorder and backorderDateFromApi is true.
        backorderCollection: item?.earliest_collection ?? null,
        backorderDelivery: item?.earliest_delivery ?? null,
      }
    },
    [session?.postcode],
  )

  // Handle adding to cart. A product is limited to ONE branch in the cart at a
  // time: if it's already in the cart, we update that single line (moving it to
  // the chosen branch when different) instead of creating a duplicate. The
  // "prefer the line at this branch, else any branch" ordering guarantees a
  // move never targets a branch that already holds the product, so there's no
  // conflict to surface — we only ever show a friendly toast.
  const handleAddToCart = async (
    listing: SupplierListing,
    quantity: number,
    method: FulfillmentMethod,
  ) => {
    const targetBranchId = String(listing.branchId)
    const lineAtBranch = cart.find(
      item =>
        String(item.productId) === String(listing.productId) &&
        String(item.branchId) === targetBranchId,
    )
    const existingLine =
      lineAtBranch ??
      cart.find(item => String(item.productId) === String(listing.productId))

    // Already in the cart at this exact branch with the same quantity. Only
    // work to do is persisting a method flip (if any). Method is now
    // authoritative on the backend line, so a failed PATCH is not silent —
    // the update mutation's onError rolls the query cache back to the old
    // value, and getItemFulfillment reads that first. The sessionStorage
    // stamp we wrote for optimistic UI would otherwise become dead weight
    // (backend-first precedence), so we wipe it on failure to keep the
    // rendered state consistent with what actually persisted.
    if (lineAtBranch && lineAtBranch.quantity === quantity) {
      const methodChanged =
        !!lineAtBranch.id && lineAtBranch.fulfillmentMethod !== method
      if (methodChanged && lineAtBranch.id) {
        writeItemFulfillmentMethod(lineAtBranch.id, method)
        try {
          await updateCartLine(lineAtBranch.id, { fulfillmentMethod: method })
        } catch {
          removeItemFulfillmentMethod(lineAtBranch.id)
          addToast(
            `Couldn't switch "${listing.productName}" to ${method === 'pickup' ? 'collection' : 'delivery'}. Please try again.`,
            'error',
            3000,
          )
          return
        }
      }
      addToast(`"${listing.productName}" is already in your basket`, 'info', 2000)
      return
    }

    // Existing line (this branch → quantity only; other branch → move + set qty).
    if (existingLine?.id) {
      const branchChanged = String(existingLine.branchId) !== targetBranchId
      try {
        const updatedLine = await updateCartLine(existingLine.id, {
          branchId: branchChanged ? targetBranchId : undefined,
          quantity,
          // Method rides the same PATCH so a single 200 covers everything —
          // no follow-up round-trip and no window where the line's method
          // and quantity are out of sync in the cache.
          fulfillmentMethod: method,
        })
        if (quantity > existingLine.quantity) {
          trackStandardProductAdd(updatedLine, quantity - existingLine.quantity)
        }
        // Kept as the transitional sessionStorage fallback (see Cart.tsx
        // getItemFulfillment precedence). Backend response already carries
        // the method, but the write costs nothing and covers pre-migration
        // lines that never make it through the backend path.
        writeItemFulfillmentMethod(existingLine.id, method)
        addToast(
          branchChanged
            ? `Moved "${listing.productName}" to ${listing.branchName}`
            : `Updated "${listing.productName}" in basket`,
          'success',
          2000,
        )
      } catch (error) {
        addToast(
          `Failed to update basket: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'error',
          3000,
        )
      }
      return
    }

    // Fresh add — the method is now sent with the create request so the
    // backend stores it on the new row. The sessionStorage writes remain as
    // a same-tab fast path and a fallback for any transitional client that
    // reads before the backend response reconciles into the cache.
    writeCartFulfillmentMethod(listing.supplierId, listing.branchId, method)
    // Security: price not sent - backend fetches from catalogue
    addToCartMutation(
      {
        product_id: Number(listing.productId),
        branch_id: targetBranchId,
        quantity,
        fulfillment_method: method,
        // Analytics only — stripped before the request. Records whether the
        // engineer added something the branch actually had in stock.
        inStockAtBranch: listing.stockStatus === 'in_stock',
        productAddMode: { mode: PRODUCT_ADD_MODES.standard, productPosition: null },
      },
      {
        onSuccess: (createdItem) => {
          if (createdItem?.id) {
            writeItemFulfillmentMethod(createdItem.id, method)
          }
          addToast(`Added "${listing.productName}" to basket`, 'success', 2000)
        },
        onError: (error) => {
          addToast(
            `Failed to add to basket: ${error instanceof Error ? error.message : 'Unknown error'}`,
            'error',
            3000,
          )
        },
      },
    )
  }

  // Sort by distance per product (backend already caps to the nearest 3)
  const sortedListings: SupplierListing[] = (() => {
    if (listingsWithLiveData.length === 0) return []

    // Group by productId
    const grouped = new Map<string | number, SupplierListing[]>()
    for (const listing of listingsWithLiveData) {
      const key = listing.productId
      if (!grouped.has(key)) grouped.set(key, [])
      grouped.get(key)!.push(listing)
    }

    // Per product: sort branches by distance (backend already sends the
    // nearest 3). BM-002 AC2/AC3: a branch the engineer already has items
    // from bubbles to the top of its supplier — overrides distance so the
    // engineer consolidates the trip instead of picking a marginally
    // nearer branch.
    const filtered: SupplierListing[] = []
    for (const listings of grouped.values()) {
      const sorted = [...listings].sort((a, b) => {
        const aBasket = basketBranchIds.has(String(a.branchId)) ? 0 : 1
        const bBasket = basketBranchIds.has(String(b.branchId)) ? 0 : 1
        if (aBasket !== bBasket) return aBasket - bBasket
        return (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999)
      })
      filtered.push(...sorted)
    }

    // Sort by user's chosen sort (default: distance). BM-002: basket-branch
    // matches keep their leading position within each product regardless of
    // which sort key the engineer picks — otherwise this final flat sort
    // would overwrite the per-product basket boost done above and PORTSWOOD
    // (nearer, non-basket) would leapfrog BITTERNE/MILLBROOK (basket) even
    // when the cart already has items there. Basket check runs first so the
    // secondary price/distance ordering still applies as a tie-breaker among
    // basket branches and among non-basket branches.
    if (listingSort === 'price') {
      return filtered.sort((a, b) => {
        const aBasket = basketBranchIds.has(String(a.branchId)) ? 0 : 1
        const bBasket = basketBranchIds.has(String(b.branchId)) ? 0 : 1
        if (aBasket !== bBasket) return aBasket - bBasket
        if (a.priceUnavailable && b.priceUnavailable) return 0
        if (a.priceUnavailable) return 1
        if (b.priceUnavailable) return -1
        return (a.price ?? 999) - (b.price ?? 999)
      })
    }
    return filtered.sort((a, b) => {
      const aBasket = basketBranchIds.has(String(a.branchId)) ? 0 : 1
      const bBasket = basketBranchIds.has(String(b.branchId)) ? 0 : 1
      if (aBasket !== bBasket) return aBasket - bBasket
      return (a.distanceMiles ?? 999) - (b.distanceMiles ?? 999)
    })
  })()

  // Calculate best price and closest from VISIBLE (filtered) listings only.
  // All price comparisons use 2 decimal places so that values like £10.001
  // and £10.004 are treated as equal when they display identically to the user.
  const roundPrice = (price: number) => Math.round(price * 100) / 100

  // Order-on-demand: depot (DC) stock is orderable even when branch stock is 0,
  // so a listing with zero branch stock but positive DC is still "in stock" —
  // mirroring the search-side DC-aware verdict. Prefer capTotal (branch +
  // orderable DC), fall back to total_stock; false for suppliers with no DC data.
  // Reused by the in-stock filter/sort AND best-price eligibility so all three
  // agree on what counts as available.
  const hasOrderableDcStock = (l: SupplierListing): boolean => {
    const cap = (l as { capTotal?: number | null }).capTotal
    if (typeof cap === 'number') return cap > 0
    const total = (l as { totalStock?: number | null }).totalStock
    if (typeof total === 'number') return total > 0
    return false
  }

  const pricedListings = sortedListings.filter(l => l.price > 0)
  // Best price is a supplier-level concept: price doesn't vary across branches.
  // A supplier is eligible if ANY of its branches has stock.
  // Find the cheapest eligible supplier (productId).
  const supplierEligibility = (() => {
    const map = new Map<string | number, { price: number; hasStock: boolean }>()
    for (const l of pricedListings) {
      if (!map.has(l.productId)) {
        map.set(l.productId, { price: roundPrice(l.price), hasStock: false })
      }
      if ((l.stockStatus !== 'out_of_stock' || hasOrderableDcStock(l)) && !l.noNearbyBranch) {
        map.get(l.productId)!.hasStock = true
      }
    }
    return map
  })()

  const eligibleSuppliers = [...supplierEligibility.entries()]
    .filter(([, v]) => v.hasStock)
    .map(([productId, v]) => ({ productId, price: v.price }))

  const bestProductId = (() => {
    if (eligibleSuppliers.length === 0) return null
    const cheapestPrice = Math.min(...eligibleSuppliers.map(e => e.price))
    return eligibleSuppliers.find(e => e.price === cheapestPrice)?.productId ?? null
  })()

  // Group sortedListings by productId — one card per raw product.
  // Preserves best-first order since sortedListings is already sorted.
  // Two products from the same supplier = two separate cards.
  // Groups where every branch is confirmed out of stock are moved to the bottom.
  const productGroups: SupplierListing[][] = (() => {
    const groups: SupplierListing[][] = []
    const seen = new Set<string>()
    for (const l of sortedListings) {
      const key = String(l.productId)
      if (!seen.has(key)) {
        seen.add(key)
        groups.push(sortedListings.filter(b => String(b.productId) === key))
      }
    }

    const isGroupOutOfStock = (branches: SupplierListing[]) =>
      !branches[0].isLoadingStock &&
      branches.every(b => {
        if (b.stockStatus !== 'out_of_stock' || b.stockUnavailable) return false
        // DC-aware (same predicate as best-price eligibility): a branch with
        // orderable depot (DC) stock isn't out of stock even when branch is 0 —
        // stockStatus alone keys off the adapter's branch-only stock_quantity
        // (UKPS/CEF) and would wrongly hide order-on-demand items.
        return !hasOrderableDcStock(b)
      })

    // A supplier with no branch in range is non-orderable — treat it like an
    // out-of-stock group so it sinks below orderable listings and is hidden
    // under the in-stock-only flow (it carries stockStatus 'unavailable', not
    // 'out_of_stock', so isGroupOutOfStock alone would leave it visible and
    // sortable ahead of real stock).
    const isGroupNoNearbyBranch = (branches: SupplierListing[]) =>
      branches.every(b => b.noNearbyBranch === true)

    const isGroupNonOrderable = (branches: SupplierListing[]) =>
      isGroupOutOfStock(branches) || isGroupNoNearbyBranch(branches)

    // Group sort applies three layered rules:
    //   1. Non-orderable groups (all OOS / no nearby branch) always sink last
    //      so the engineer never sees them ahead of an orderable option.
    //   2. BM-002 AC1/AC4: within the orderable tier, groups whose supplier
    //      matches a basket item float to the top. When multiple basket
    //      suppliers are boosted, they order by price so the cheapest of the
    //      trip-consolidating choices wins.
    //   3. AC5/AC6: everything else preserves the order it inherited from
    //      sortedListings (i.e. today's price/distance sort). An empty basket
    //      makes step 2 a no-op — both sides evaluate to "not in basket" and
    //      the compare returns 0.
    const isBasketSupplierGroup = (branches: SupplierListing[]) =>
      basketSupplierIds.size > 0 &&
      branches.some((l) => basketSupplierIds.has(String(l.supplierId)))

    const sorted = groups.sort((a, b) => {
      const aOff = isGroupNonOrderable(a)
      const bOff = isGroupNonOrderable(b)
      if (aOff !== bOff) return aOff ? 1 : -1

      const aBasket = isBasketSupplierGroup(a) ? 0 : 1
      const bBasket = isBasketSupplierGroup(b) ? 0 : 1
      if (aBasket !== bBasket) return aBasket - bBasket

      if (aBasket === 0) {
        // Both in basket → tie-break on price so multiple basket suppliers
        // present themselves cheapest-first per AC4.
        return (a[0].price ?? 999) - (b[0].price ?? 999)
      }

      return 0
    })

    // When the in-stock filter was active on the search page, hide supplier
    // offerings that can't be ordered here: every branch confirmed out of
    // stock, or no branch in range. Groups still loading stock are kept visible
    // so they are not prematurely removed before the API response arrives.
    return inStockFilterActive
      ? sorted.filter((branches) => !isGroupNonOrderable(branches))
      : sorted
  })()

  // Show badges when there are multiple cards to compare
  const hasMultipleListings = productGroups.length > 1

  // Mirrors the card's firstStockedBranchId logic: nearest branch with any
  // orderable stock (primary OR DC-backed), falling back to branches[0] when
  // loading or all out-of-stock. Uses `capTotal` — the view-computed ceiling
  // that already covers branch shelf, branch-deliverable, and DC stock —
  // instead of primary-only, otherwise a DC-only branch would be treated as
  // empty here while the card correctly picks it as the first stocked branch.
  const nearestStockedBranch = (branches: SupplierListing[]): SupplierListing => {
    const head = branches[0]
    if (head.isLoadingStock) return head
    return branches.find(b => (b.capTotal ?? 0) > 0) ?? head
  }

  // Closest distance across products — based on each product's nearest STOCKED branch,
  // because that is what the card actually displays (price is product-level, distance is branch-level).
  const closestDistance = (() => {
    const distances = productGroups
      .map(branches => nearestStockedBranch(branches).distanceMiles)
      .filter((d): d is number => d != null)
    return distances.length > 0 ? Math.min(...distances) : 999
  })()

  // Skip badge when all eligible suppliers (those with any stock) share the same price
  const allPricesIdentical = eligibleSuppliers.length > 1 &&
    eligibleSuppliers.every(e => e.price === eligibleSuppliers[0].price)

  const isBestPrice = (listing: SupplierListing) =>
    bestProductId != null && listing.productId === bestProductId

  const isClosest = (listing: SupplierListing) =>
    listing.distanceMiles != null && listing.distanceMiles === closestDistance

  // Pick a sensible default method for a group: prefer collection when the
  // active listing exposes it, else delivery. When both are unavailable
  // (checking / no branch in range), fall through to pickup so the card still
  // shows a radio dot when a group eventually becomes orderable.
  const defaultMethodFor = (listing: SupplierListing): FulfillmentMethod => {
    if (listing.canCollect !== false) return 'pickup'
    if (listing.canDeliver !== false) return 'delivery'
    return 'pickup'
  }

  // Auto-select the first orderable group once product groups settle. Also
  // heals a stale selection (previous supplier no longer present in the list
  // after a sort change / filter change).
  useEffect(() => {
    if (productGroups.length === 0) return
    // Preferred branch from the search page (see preferredBranchId). A
    // master can have several listings from one supplier (pack sizes), and
    // the search matched on whichever one is orderable at that branch — so
    // prefer the listing that is stocked or backorderable THERE, not merely
    // the first listing that has a row for the branch. While any candidate
    // is still loading stock, keep waiting (the default selection below
    // fills in meanwhile); once everything has loaded with no orderable
    // candidate, give up and leave the default. Never overrides a branch the
    // engineer has since chosen by hand.
    if (
      preferredBranchId &&
      !preferredBranchAppliedRef.current &&
      !manualSelectionRef.current
    ) {
      const candidates = productGroups
        .map((branches) =>
          branches.find((branch) => String(branch.branchId) === preferredBranchId),
        )
        .filter((listing): listing is SupplierListing => listing != null)
      // Method must be the one that makes the candidate orderable, using
      // the same per-channel capacity rule as the footer (max of channel
      // stock and its DC tier, gated by canCollect / canDeliver): a branch
      // with delivery stock but none for pickup must open on delivery, and
      // a branch that can only backorder for one channel must open on that
      // channel — or the footer would disable Add for the chosen method.
      const orderableMethodFor = (listing: SupplierListing): FulfillmentMethod | null => {
        const pickupOption = listing.fulfillmentOptions?.find((option) => option.type === 'pickup')
        const deliveryOption = listing.fulfillmentOptions?.find(
          (option) => option.type === 'delivery',
        )
        const pickupQty =
          listing.canCollect !== false
            ? Math.max(pickupOption?.stockQuantity ?? 0, listing.pickupDcStock ?? 0)
            : 0
        const deliveryQty =
          listing.canDeliver !== false
            ? Math.max(deliveryOption?.stockQuantity ?? 0, listing.deliveryDcStock ?? 0)
            : 0
        if (pickupQty > 0) return 'pickup'
        if (deliveryQty > 0) return 'delivery'
        if (listing.backorderAllowed !== true) return null
        if (listing.backorderCollectionDate != null && listing.canCollect !== false) {
          return 'pickup'
        }
        if (listing.backorderDeliveryDate != null && listing.canDeliver !== false) {
          return 'delivery'
        }
        return null
      }
      let orderable: SupplierListing | undefined
      let orderableMethod: FulfillmentMethod | null = null
      for (const listing of candidates) {
        orderableMethod = orderableMethodFor(listing)
        if (orderableMethod !== null) {
          orderable = listing
          break
        }
      }
      if (orderable && orderableMethod !== null) {
        preferredBranchAppliedRef.current = true
        setSelection({
          productId: orderable.productId,
          branchId: orderable.branchId,
          method: orderableMethod,
        })
        return
      }
      const stillLoading = candidates.some((listing) => listing.isLoadingStock)
      if (!stillLoading && candidates.length > 0) {
        preferredBranchAppliedRef.current = true
      }
    }
    const stillPresent =
      selection != null &&
      productGroups.some((branches) => branches[0].productId === selection.productId)
    if (stillPresent) return
    const head = productGroups[0]
    const nearest = nearestStockedBranch(head)
    setSelection({
      productId: head[0].productId,
      branchId: nearest.branchId,
      method: defaultMethodFor(nearest),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productGroups])

  // Resolve the currently selected listing/group for the sticky footer.
  const selectedGroup =
    selection != null
      ? productGroups.find((branches) => branches[0].productId === selection.productId)
      : undefined
  const selectedListing: SupplierListing | undefined = selectedGroup
    ? selectedGroup.find((b) => String(b.branchId) === String(selection?.branchId)) ??
      selectedGroup[0]
    : undefined

  // Selection is captured while stock is still loading, so `selection.branchId`
  // is often `null`. Once live stock resolves the card auto-picks its nearest
  // stocked branch; keep the page-level selection in sync so the footer
  // prices/submits against the same branch the card is displaying. Only kicks
  // in when the current selection.branchId is missing or not present in the
  // resolved group — a manual branch pick still wins.
  useEffect(() => {
    if (!selection || !selectedGroup) return
    const branchInGroup = selectedGroup.some(
      (b) => String(b.branchId) === String(selection.branchId),
    )
    if (selection.branchId != null && branchInGroup) return
    const nearest = nearestStockedBranch(selectedGroup)
    if (nearest.branchId == null) return
    setSelection((prev) =>
      prev
        ? {
            ...prev,
            branchId: nearest.branchId,
            method: defaultMethodFor(nearest),
          }
        : prev,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup])

  // Seed the footer stepper when the engineer picks a different SUPPLIER.
  // Behavior:
  //   - Cart line exists for the new supplier → hydrate from it.
  //   - First-ever selection → seed the supplier's quantity multiple (else 1)
  //     so pack-size products don't land with a disabled Add button.
  //   - Otherwise (supplier switch mid-session) → keep the typed quantity.
  //     The footer's isOverStock / isInvalidMultiple already surface any
  //     mismatch, so silently resetting to 1 (DEV-50862) loses intent when
  //     the engineer switches to a supplier that has enough stock.
  // Keyed on productId only — a branchId-only change (either the engineer
  // swapping branches within a supplier, or the card's qty-aware auto-swap
  // bubbling up) must NOT reset qty, otherwise typing "20" against a branch
  // that holds 5 triggers the auto-swap and then this effect snaps qty back
  // to 1, undoing the swap. `cart` is intentionally omitted from the deps:
  // reseeding on every cart mutation would clobber the value the engineer
  // is currently typing.
  const prevSelectedProductIdRef = useRef<string | number | undefined>(undefined)
  useEffect(() => {
    if (!selectedListing) return
    const prevProductId = prevSelectedProductIdRef.current
    const productChanged = prevProductId !== selection?.productId
    prevSelectedProductIdRef.current = selection?.productId
    if (!productChanged) return
    const line =
      cart.find(
        (item) =>
          String(item.productId) === String(selectedListing.productId) &&
          String(item.branchId) === String(selectedListing.branchId),
      ) ?? cart.find((item) => String(item.productId) === String(selectedListing.productId))
    if (line) {
      setQuantity(line.quantity)
      return
    }
    // First-time selection seeds the multiple so pack-size products don't
    // load with a disabled Add ("Sold in multiples of N"). Subsequent
    // supplier switches preserve the engineer's typed quantity — the footer
    // already surfaces isOverStock / isInvalidMultiple if it no longer fits.
    if (prevProductId === undefined) {
      setQuantity(selectedListing.quantityMultiple ?? 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection?.productId, selection?.branchId])

  // Derived footer state — button label, disabled reasons, running total.
  const orderMultiple = selectedListing?.quantityMultiple ?? 1
  // Per-method capacity: MAX of primary channel stock and DC/later tier,
  // gated by canCollect/canDeliver. Uses MAX not SUM because adapters differ:
  // CPS pre-subtracts the branch portion from dcStock, but Wolseley/UKPS
  // report a raw depot pool that already includes branch stock. Summing would
  // double-count for the raw-pool suppliers and enable quantities the
  // backend's `cap_total` (also a max) rejects at order-place. MAX is the
  // safe direction — slightly under-restrictive for CPS in edge cases, but
  // no false positives.
  const selectedPickupOption = selectedListing?.fulfillmentOptions?.find(
    (option) => option.type === 'pickup',
  )
  const selectedDeliveryOption = selectedListing?.fulfillmentOptions?.find(
    (option) => option.type === 'delivery',
  )
  const canCollectSelected = selectedListing?.canCollect !== false
  const canDeliverSelected = selectedListing?.canDeliver !== false
  const pickupMaxQty = canCollectSelected
    ? Math.max(
        selectedPickupOption?.stockQuantity ?? 0,
        selectedListing?.pickupDcStock ?? 0,
      )
    : 0
  const deliveryMaxQty = canDeliverSelected
    ? Math.max(
        selectedDeliveryOption?.stockQuantity ?? 0,
        selectedListing?.deliveryDcStock ?? 0,
      )
    : 0
  const methodMaxQty = selection?.method === 'delivery' ? deliveryMaxQty : pickupMaxQty
  // Backorder: when the supplier/product allows it (and the branch serves the
  // channel), a quantity above stock — or zero stock — is still orderable.
  // Everything else (max order qty, pack multiples, stock-check failures)
  // still applies.
  // A backorder needs an availability date for the channel. Use the card's
  // resolved date when it has reported (qty-aware for Wolseley), else the
  // listing's own; while the card is still checking, Add is already blocked
  // by isCheckingDcDateSelected below.
  const selectedBackorderDate = (() => {
    if (!selectedListing || !selection) return null
    const resolved = resolvedDatesByBranch.get(
      `${selectedListing.supplierId}-${selectedListing.branchId}`,
    )
    // The card reports the backorder date on its own (null when the line is
    // not a backorder or the qty-matched quote is missing) — never the
    // general branch/depot date.
    if (resolved && !resolved.isChecking) {
      return selection.method === 'delivery'
        ? resolved.backorderDelivery
        : resolved.backorderPickup
    }
    return selection.method === 'delivery'
      ? (selectedListing.backorderDeliveryDate ?? null)
      : (selectedListing.backorderCollectionDate ?? null)
  })()
  const canBackorderSelected =
    selectedListing?.backorderAllowed === true &&
    (selection?.method === 'delivery' ? canDeliverSelected : canCollectSelected) &&
    selectedBackorderDate != null
  const isOverStock = quantity > methodMaxQty && methodMaxQty > 0 && !canBackorderSelected
  const isInvalidMultiple =
    orderMultiple > 1 && quantity > 0 && quantity % orderMultiple !== 0
  const existingCartLine = selectedListing
    ? cart.find(
        (item) =>
          String(item.productId) === String(selectedListing.productId) &&
          String(item.branchId) === String(selectedListing.branchId),
      ) ?? cart.find((item) => String(item.productId) === String(selectedListing.productId))
    : undefined
  const isNoNearbyBranchSelected = selectedListing?.noNearbyBranch === true
  const isStockUnavailableSelected =
    selectedListing?.stockUnavailable === true ||
    !!(selectedListing as { stockError?: string } | undefined)?.stockError
  const isCheckingStockSelected = selectedListing?.isLoadingStock === true
  // Card's in-flight DC-quantity check for the selected branch. Gates canAdd
  // so we can't tap Add while the effective fulfillment date is still
  // resolving (the footer would price/label against the stale branch date).
  const selectedResolvedKey = selectedListing
    ? `${selectedListing.supplierId}-${selectedListing.branchId}`
    : null
  const isCheckingDcDateSelected =
    selectedResolvedKey != null &&
    (resolvedDatesByBranch.get(selectedResolvedKey)?.isChecking ?? false)
  const canAdd =
    selectedListing != null &&
    !isCheckingStockSelected &&
    !isCheckingDcDateSelected &&
    !isStockUnavailableSelected &&
    !isNoNearbyBranchSelected &&
    !isOverStock &&
    !isInvalidMultiple &&
    quantity > 0 &&
    (methodMaxQty > 0 || canBackorderSelected) &&
    // Guard rapid double-taps — the mutation isn't disabling the button
    // by itself, so without this each tap fires another /items/ POST and
    // (via the cart-cache churn) rebooks the DC recheck timer.
    !isAdding &&
    !isUpdating
  const footerButtonLabel = (() => {
    if (isCheckingStockSelected) return 'Checking stock…'
    if (isCheckingDcDateSelected) return 'Checking availability…'
    if (isNoNearbyBranchSelected) return 'No branch in range'
    if (isStockUnavailableSelected) return 'Out of stock'
    if (methodMaxQty <= 0 && !canBackorderSelected) {
      return selection?.method === 'delivery' ? 'Not available for delivery' : 'Not available for collection'
    }
    if (isOverStock) return `Only ${methodMaxQty.toLocaleString()} available`
    if (isInvalidMultiple) return `Sold in multiples of ${orderMultiple}`
    const verb = existingCartLine ? 'Update' : 'Add'
    return `${verb} ${quantity} for ${selection?.method === 'delivery' ? 'delivery' : 'collection'}`
  })()
  const footerSummary = (() => {
    if (!selectedListing || !selection) return null
    const methodLabel = selection.method === 'delivery' ? 'Delivery' : 'Collection'
    // Prefer the card-resolved effective date (DC-aware for qty > branch);
    // fall back to the branch-level option date only if the card hasn't
    // reported yet.
    const key = `${selectedListing.supplierId}-${selectedListing.branchId}`
    const resolved = resolvedDatesByBranch.get(key)
    const effectiveDate =
      selection.method === 'delivery' ? resolved?.delivery : resolved?.pickup
    const fallbackDate = selectedListing.fulfillmentOptions?.find(
      (o) => o.type === selection.method,
    )?.earliestDate
    const dateLabel =
      formatEarliestDate(effectiveDate ?? fallbackDate) ??
      (selection.method === 'delivery' ? 'TBC' : 'Collection date to be confirmed')
    // Total uses per-BASE-unit price so pack sizes don't overstate the cost:
    // a £5 pack of 10 with qty=10 base units = £5, not £50. Mirrors the
    // cart page's math (which also drives the cart's line totals).
    const total = perUnitPrice(selectedListing.price ?? 0, orderMultiple) * quantity
    return { methodLabel, dateLabel, total }
  })()

  const handleFooterAdd = () => {
    if (!selectedListing || !canAdd || !selection) return
    // Fulfillment write is delegated to handleAddToCart so it can pick
    // per-item vs. group-level based on whether this is an update to an
    // existing cart line (per-item, avoids cross-contamination) or a
    // fresh add (group-level default until the new line's id lands).
    handleAddToCart(selectedListing, quantity, selection.method)
  }

  if (!session) {
    return null
  }

  return (
    <EngineerLayout hideHeader>
      {/* Header with Back Button — themed via `.bg-nav` / `.text-nav`.
       * Shared EngineerHeader is suppressed so the back+title bar is the
       * only header on this page. */}
      <div className="bg-nav text-nav border-b border-nav-divider sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 text-nav hover:bg-nav-hover rounded-xl transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-nav font-bold truncate flex-1">
              {product?.displayName || 'Loading...'}
            </h1>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 pt-3 pb-24">
        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary mb-3" />
            <p className="text-gray-500 text-sm">Loading product details...</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-gray-900 text-lg font-bold mb-2">
              Failed to load product
            </h2>
            <p className="text-gray-500 text-sm mb-4 text-center">
              We couldn't load the product details. Please try again.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Go back
              </Button>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* Not Found State - API returned null */}
        {!isLoading && !error && product === null && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-gray-900 text-lg font-bold mb-2">
              Product not found
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              The product you're looking for doesn't exist or has been removed.
            </p>
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go back
            </Button>
          </div>
        )}

        {/* Product Content */}
        {product && (
          <>
            {/* Product Header Card */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-3">
              {/* Product Image Carousel - Swipeable on mobile */}
              <div
                className="bg-gray-100 w-full aspect-[4/3] sm:aspect-[5/2] max-h-56 relative overflow-hidden"
                onTouchStart={(e) => {
                  if (!product.images || product.images.length <= 1) return
                  const touch = e.touches[0]
                  const startX = touch.clientX

                  const handleTouchMove = (moveEvent: TouchEvent) => {
                    const currentX = moveEvent.touches[0].clientX
                    const diff = startX - currentX

                    if (Math.abs(diff) > 50) {
                      if (diff > 0) {
                        // Swipe left - next image
                        setCurrentImageIndex(prev =>
                          prev === product.images!.length - 1 ? 0 : prev + 1
                        )
                      } else {
                        // Swipe right - previous image
                        setCurrentImageIndex(prev =>
                          prev === 0 ? product.images!.length - 1 : prev - 1
                        )
                      }
                      cleanup()
                    }
                  }

                  const handleTouchEnd = () => {
                    cleanup()
                  }

                  const cleanup = () => {
                    document.removeEventListener('touchmove', handleTouchMove)
                    document.removeEventListener('touchend', handleTouchEnd)
                  }

                  document.addEventListener('touchmove', handleTouchMove)
                  document.addEventListener('touchend', handleTouchEnd)
                }}
              >
                {product.images && product.images.length > 0 ? (
                  <>
                    {/* Images */}
                    <div
                      className="flex h-full transition-transform duration-300 ease-out"
                      style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                    >
                      {product.images.map((image, index) => (
                        <div key={image.id} className="w-full h-full flex-shrink-0 relative">
                          <img
                            src={image.url}
                            alt={`${product.displayName} - Image ${index + 1}`}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden') }}
                          />
                          <div className="hidden w-full h-full flex flex-col items-center justify-center bg-gray-50 absolute inset-0">
                            <Package className="w-16 h-16 text-gray-300 mb-2" />
                            <p className="text-gray-400 text-sm">No image available</p>
                          </div>
                          {image.supplier_name && (
                            <span className="absolute bottom-1.5 left-1.5 bg-black/50 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full leading-none z-10">
                              {image.supplier_name}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Navigation arrows (desktop) */}
                    {product.images.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(prev =>
                            prev === 0 ? product.images!.length - 1 : prev - 1
                          )}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hidden sm:block z-10"
                          aria-label="Previous image"
                        >
                          <ChevronLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(prev =>
                            prev === product.images!.length - 1 ? 0 : prev + 1
                          )}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all hidden sm:block z-10"
                          aria-label="Next image"
                        >
                          <ChevronRight className="w-5 h-5 text-gray-700" />
                        </button>
                      </>
                    )}

                    {/* Dots indicator */}
                    {product.images.length > 1 && (
                      <div className="absolute bottom-3 left-0 right-0 flex justify-center z-10">
                        <div className="bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full flex gap-1.5">
                          {product.images.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={clsx(
                                'h-2 rounded-full transition-all',
                                index === currentImageIndex
                                  ? 'bg-white w-6'
                                  : 'bg-white/60 w-2'
                              )}
                              aria-label={`Go to image ${index + 1}`}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : product.imageUrl ? (
                  <img
                    src={product.imageUrl}
                    alt={product.displayName}
                    className="w-full h-full object-contain p-4"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                    <Package className="w-16 h-16 text-gray-300 mb-2" />
                    <p className="text-gray-400 text-sm">No image available</p>
                  </div>
                )}
              </div>

              {/* Product Info */}
              <div className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-3">
                  <h2 className="text-gray-900 text-lg font-bold">{product.displayName}</h2>
                  <MasterFavouriteHeartButton
                    masterProductId={product.id}
                    productName={product.displayName}
                  />
                </div>

                {/* Product Code */}
                {product.canonicalMpn && (
                  <p className="text-gray-400 text-xs mt-0.5">{product.canonicalMpn}</p>
                )}

                {/* Product info accordion — carries Brand (DEV-51596) and the
                    long description. Shown even when the product has no text
                    description so the brand row always has a home, with a
                    "-" fallback (AC2). */}
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowDescription(prev => !prev)}
                    className={clsx(
                      'w-full flex items-center justify-between px-3 py-2 bg-gray-50 transition-colors',
                      showDescription ? 'rounded-t-md' : 'rounded-md'
                    )}
                  >
                    <span className="text-xs font-medium text-gray-500">Product info</span>
                    <ChevronDown className={clsx(
                      'w-3.5 h-3.5 text-gray-400 transition-transform duration-200',
                      showDescription && 'rotate-180'
                    )} />
                  </button>
                  <div className={clsx(
                    'grid transition-all duration-200 ease-in-out',
                    showDescription ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}>
                    <div className="overflow-hidden">
                      <div className="px-3 py-2.5 bg-gray-50 rounded-b-md space-y-1.5">
                        <p className="text-gray-600 text-xs leading-relaxed">
                          <span className="font-medium text-gray-700">Brand:</span>{' '}
                          {product.brand || '-'}
                        </p>
                        {product.description && (
                          <p className="text-gray-600 text-xs leading-relaxed">
                            {product.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quantity selector — pulled out of the sticky footer so the
                 * engineer states qty BEFORE scanning supplier/branch options.
                 * With qty declared up-front, each supplier tile can also
                 * default to its nearest branch that actually fulfils that
                 * quantity. Lives inside the product-info card so it reads
                 * as part of the master product context. */}
                <div className="mt-3">
                  <h3 className="text-gray-800 font-bold text-xs mb-2">Choose your quantity</h3>
                  {/* No `stock` cap here — the engineer states qty BEFORE
                   * picking a supplier, so a per-supplier ceiling would be
                   * meaningless. Over-stock is surfaced on the supplier
                   * tiles + sticky footer button label instead.
                   * `fullWidth` stretches the stepper to the card, swaps the
                   * pill shape for a softer radius, and widens the +/- so
                   * the input doesn't read as a lonely text field. */}
                  <QuantitySelector
                    value={quantity}
                    onChange={(next) =>
                      setQuantity(next ?? Math.max(orderMultiple, 1))
                    }
                    multiple={orderMultiple}
                    size="md"
                    fullWidth
                  />
                </div>
              </div>
            </div>

            {/* Supplier Selection Section */}
            <div className="mb-4">
              {/* Section header + sort toggle — hidden entirely when only one
                  supplier is present (nothing to compare or sort). Count is
                  distinct SUPPLIER ids, not productGroups.length: a master
                  can have multiple matched raw products from the same
                  supplier (e.g. two Wolseley SKUs), and calling those two
                  "Suppliers" would be wrong. The prominent size + count also
                  signals "more results below" on mobile where the first card
                  can otherwise read as a false bottom. */}
              {(() => {
                const distinctSupplierCount = new Set(
                  productGroups.map(g => String(g[0].supplierId)),
                ).size
                if (distinctSupplierCount < 2) return null
                return (
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-900 font-bold text-base">Choose from {distinctSupplierCount} suppliers</h3>
                  <ToggleGroup.Root
                    type="single"
                    value={listingSort}
                    onValueChange={(v) => v && setListingSort(v as 'distance' | 'price')}
                    className="flex gap-1.5"
                  >
                    <ToggleGroup.Item
                      value="distance"
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all data-[state=on]:bg-gray-900 data-[state=on]:text-white data-[state=off]:text-gray-500 data-[state=off]:hover:bg-gray-100"
                    >
                      <Navigation className="w-3 h-3" />
                      Distance
                    </ToggleGroup.Item>
                    <ToggleGroup.Item
                      value="price"
                      className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all data-[state=on]:bg-gray-900 data-[state=on]:text-white data-[state=off]:text-gray-500 data-[state=off]:hover:bg-gray-100"
                    >
                      £ Price
                    </ToggleGroup.Item>
                  </ToggleGroup.Root>
                </div>
                )
              })()}

              {/* Supplier Listings as Cards */}
              {sortedListings.length > 0 ? (
                <>
                  <div className="space-y-3">
                    {(showAllListings ? productGroups : productGroups.slice(0, 10)).map((branches) => {
                      const defaultListing = branches[0]
                      const isThisBestPrice = isBestPrice(defaultListing) && hasMultipleListings && !allPricesIdentical
                      const isThisClosest = isClosest(nearestStockedBranch(branches)) && hasMultipleListings
                      const isThisSelected = selection?.productId === defaultListing.productId

                      return (
                        <div key={String(defaultListing.productId)}>
                          <SupplierListingCard
                            listing={defaultListing}
                            branches={branches}
                            isBestPrice={isThisBestPrice}
                            isSamePrice={false}
                            isClosest={isThisClosest}
                            cart={cart}
                            quantity={quantity}
                            isSelected={isThisSelected}
                            selectedMethod={
                              isThisSelected ? selection?.method : undefined
                            }
                            onSelect={(listing, method) => {
                              manualSelectionRef.current = true
                              setSelection({
                                productId: listing.productId,
                                branchId: listing.branchId,
                                method,
                              })
                            }}
                            onBranchChange={(listing) => {
                              // Keep the current method when the engineer only
                              // swaps branch inside the picker; select this
                              // group if it wasn't the active one already.
                              manualSelectionRef.current = true
                              setSelection((prev) => ({
                                productId: listing.productId,
                                branchId: listing.branchId,
                                method:
                                  prev?.productId === listing.productId
                                    ? prev.method
                                    : defaultMethodFor(listing),
                              }))
                            }}
                            onCheckQuantity={handleCheckQuantity}
                            onResolvedDates={handleResolvedDates}
                          />
                        </div>
                      )
                    })}
                  </div>

                  {/* Show More Button */}
                  {productGroups.length > 10 && !showAllListings && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => setShowAllListings(true)}
                      >
                        Show {productGroups.length - 10} More Supplier{productGroups.length - 10 !== 1 ? 's' : ''}
                      </Button>
                    </div>
                  )}

                  {/* Show Less Button */}
                  {showAllListings && productGroups.length > 10 && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={() => {
                          setShowAllListings(false)
                          window.scrollTo({ top: 0, behavior: 'smooth' })
                        }}
                      >
                        Show less
                      </Button>
                    </div>
                  )}

                  {/* Subtle error message if stock API failed */}
                  {isStockError && (
                    <p className="text-gray-400 text-xs text-center mt-3">
                      Stock information unavailable
                    </p>
                  )}
                </>
              ) : (
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-6 text-center">
                  <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">No suppliers available</p>
                  <p className="text-gray-400 text-sm">
                    This product is currently not available from any supplier.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      {/* Sticky action footer — page-level qty + Add to cart for the currently
       * selected offer. Sits directly above the EngineerLayout bottom nav
       * (bottom-16) with a soft up-shadow so it reads as a distinct surface.
       * z-30 keeps it below the nav's z-40 so it never overlaps navigation. */}
      {product && selectedListing && (
        <div
          ref={toastClearanceRef}
          className="fixed left-0 right-0 z-30 border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(32,26,46,0.14)]"
          style={{ bottom: '4rem' }}
        >
          <div className="max-w-3xl mx-auto px-4 pt-3 pb-3">
            {/* Supplier · branch · total strip — sits ABOVE the CTA so the
             * commercial context (who / where / how much) is the first thing
             * the engineer reads before committing. Method + date stay under
             * the button as the fulfilment context. */}
            {footerSummary && (
              // Flex + shrink-0 on supplier and Total so only the branch name
              // truncates when the row overflows on narrow screens. The total
              // is the one number the engineer needs here — the branch name
              // is already visible in the card above.
              <div className="mb-2 flex min-w-0 items-center justify-center text-xs leading-tight text-gray-600">
                <span className="shrink-0 font-bold text-gray-900">
                  {selectedListing.supplierName}
                </span>
                {selectedListing.branchName && (
                  <span className="min-w-0 truncate">
                    {' · '}
                    {selectedListing.branchName}
                  </span>
                )}
                <span className="shrink-0">
                  {' · Total '}
                  <span className="font-bold text-brand-primary">
                    £{footerSummary.total.toFixed(2)}
                  </span>
                </span>
              </div>
            )}

            {/* Add / Update button — the qty stepper now lives at the top of
             * the page (above the supplier picker), so the footer is CTA-only. */}
            <button
              type="button"
              onClick={handleFooterAdd}
              disabled={!canAdd}
              className="min-h-10 w-full rounded-full px-3 text-xs font-bold bg-brand-primary hover:bg-brand-primary-hover active:bg-brand-primary-dark disabled:opacity-50 text-[var(--brand-primary-text)] transition-colors"
            >
              {footerButtonLabel}
            </button>

            {/* Fulfilment context under the CTA: method · date. */}
            {footerSummary && (
              <div className="mt-2 min-w-0 truncate text-center text-xs leading-tight text-gray-600">
                {footerSummary.methodLabel}
                {' · '}
                {footerSummary.dateLabel}
              </div>
            )}
          </div>
        </div>
      )}
    </EngineerLayout>
  )
}

function MasterFavouriteHeartButton({
  masterProductId,
  productName,
}: {
  masterProductId: string | number
  productName: string
}) {
  const { isFavourited } = useFavourites()
  const toggleFavourite = useFavouriteToggle()
  const target = { type: 'master' as const, id: masterProductId }
  const active = isFavourited(target)
  return (
    <button
      type="button"
      onClick={() => toggleFavourite({ target, productName })}
      // The ::before extends the tap area to 44px without changing the look.
      className="relative p-1 -m-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 before:absolute before:-inset-2.5 before:content-['']"
      aria-label={active ? 'Remove from favourites' : 'Add to favourites'}
      aria-pressed={active}
    >
      <Heart
        className={clsx(
          'w-5 h-5',
          active ? 'text-brand-primary' : 'text-gray-400',
        )}
        fill={active ? 'currentColor' : 'none'}
      />
    </button>
  )
}
